import React from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  IconButton,
  Snackbar,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { useTranslation } from 'react-i18next';
import { useThemeMode } from '../../theme/ThemeContext';
import { LanguagePicker } from './LanguagePicker';
import { ModelSetSettings } from './ModelSetSettings';
import { useIdentity } from '../../app/auth/sessionIdentity';

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
  onClearProjects?: () => void;
  // ⛔ `onOpenAccessGrants` WAS HERE AND IS FULL-DELETED (2026-08-29, operator ruling R-Q15, FULL
  // REMOVAL of the magic-link plane). Its doc read: "When provided, renders an Administration section
  // with a Magic Link Manager row that closes Settings and invokes this callback. Caller (PrdApp)
  // only passes this when ?mode=admin is set; non-admins never see the row." ⚠️ That last sentence was
  // ALREADY STALE before this removal — WI-32 (#2002, 2026-08-20) full-deleted the client-asserted
  // `?mode=admin` URL source and re-pointed the gate at `useIdentity().isAdmin`, and this copy of
  // the doc was never corrected. Recorded rather than dropped so the stale claim is not left to be
  // rediscovered as if it had been true.
}

export function SettingsDialog({ open, onClose, onClearProjects }: SettingsDialogProps) {
  const theme = useTheme();
  // The dialog is an elevated surface (#333 in dark), where the tertiary tier measures 4.2:1; one tier up clears 4.5.
  const dark = theme.palette.mode === 'dark';
  const quiet = dark ? theme.custom.colors.text.secondary : theme.custom.colors.text.tertiary;
  const dangerInk = dark ? theme.custom.colors.error[800] : theme.custom.colors.error.main;
  const { t } = useTranslation();
  const identity = useIdentity();
  const { mode, toggleTheme } = useThemeMode();
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

  // Style guide §6.6: overline section label
  const sectionLabel = {
    fontSize: theme.custom.typography.typeRoles.overline.size,
    fontWeight: theme.custom.typography.fontWeight.semibold,
    color: quiet,
    textTransform: 'uppercase' as const,
    letterSpacing: theme.custom.typography.typeRoles.overline.letterSpacing,
    mb: 1,
  };

  // Style guide §6.3: setting row — card-like container
  const settingRow = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    px: 2,
    py: 1.5,
    borderRadius: theme.custom.borders.radius.lg,
    border: `1px solid ${theme.custom.colors.border.light}`,
    bgcolor: theme.custom.colors.background.paper,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.custom.borders.radius.lg,
          // Dark: background.secondary is #404040, under 4.5:1 for the secondary tier; the elevated surface is #333.
          bgcolor: dark ? theme.custom.colors.accent.surfaceHi : theme.custom.colors.background.secondary,
          boxShadow: theme.custom.shadows.lg,
        },
      }}
    >
      {/* §6.6 Modal header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${theme.custom.colors.border.light}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: theme.custom.typography.fontWeight.semibold,
            fontSize: theme.custom.typography.typeRoles.section.size,
            color: theme.custom.colors.text.primary,
          }}
        >
          {t('common:settings.title')}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: quiet,
            '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.06) },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Appearance */}
          <Box>
            <Typography sx={sectionLabel}>{t('common:settings.appearance')}</Typography>
            <Box sx={settingRow}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                {mode === 'light'
                  ? <DarkModeOutlinedIcon sx={{ fontSize: 20, color: quiet, flexShrink: 0 }} />
                  : <LightModeOutlinedIcon sx={{ fontSize: 20, color: theme.custom.colors.warning.main, flexShrink: 0 }} />
                }
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: theme.custom.typography.fontWeight.semibold,
                      fontSize: theme.custom.typography.typeRoles.body.size,
                      color: theme.custom.colors.text.primary,
                      lineHeight: 1.4,
                    }}
                  >
                    {t('common:settings.darkMode')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: theme.custom.typography.typeRoles.caption.size,
                      color: quiet,
                      lineHeight: 1.3,
                    }}
                  >
                    {mode === 'light' ? t('common:settings.darkModeOffDescription') : t('common:settings.darkModeOnDescription')}
                  </Typography>
                </Box>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={mode === 'dark'}
                    onChange={toggleTheme}
                    size="small"
                  />
                }
                label=""
                sx={{ mr: 0, ml: 0, flexShrink: 0 }}
              />
            </Box>
            {/* fr-locale Phase 3a: language picker as a second row in the Appearance section */}
            <Box sx={{ mt: 1.5 }}>
              <LanguagePicker rowSx={settingRow} />
            </Box>
          </Box>

          {/* Model set: the default for new workspaces, drawn from the backend's own list. Administrators
              only (the backend refuses anyone else's write): for everyone else the row does not exist, and
              until /api/me has answered nobody is shown it. */}
          {!identity.loading && identity.isAdmin && <ModelSetSettings rowSx={settingRow} quiet={quiet} />}

          {/* ⛔ THE ADMINISTRATION SECTION WAS HERE AND IS FULL-DELETED (2026-08-29, operator
              ruling R-Q15). It was gated on the `onOpenAccessGrants` prop and held one row — the
              Magic Link Manager — whose Open button closed Settings and raised `AccessGrantsDialog`.
              The dialog, the prop and the plane behind them are all gone, so the section is removed
              rather than left as a block that can never render. Its four i18n keys
              (`settings.administration`, `settings.magicLinks`, `settings.magicLinksDescription`,
              `settings.open`) are deleted from all THREE locales in the same pass — the locale-parity
              and key-resolution tests require call site and key to move together. */}

          {/* Danger Zone — ONE row: the single purge-everything action (operator ruling 2026-10-25).
              `PrdApp` passes the prop ONLY for an administrator, so a non-admin sees no row at all
              rather than a disabled one. It is a UI signal; the endpoint fail-closes independently. */}
          {onClearProjects && (
            <Box>
              <Typography sx={{ ...sectionLabel, color: dangerInk }}>
                {t('common:settings.dangerZone')}
              </Typography>
              <Box
                sx={{
                  ...settingRow,
                  borderColor: alpha(theme.palette.error.main, 0.3),
                  bgcolor: alpha(theme.palette.error.main, 0.03),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <DeleteForeverOutlinedIcon sx={{ fontSize: 20, color: theme.custom.colors.error.main, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: theme.custom.typography.fontWeight.semibold,
                        fontSize: theme.custom.typography.typeRoles.body.size,
                        color: theme.custom.colors.text.primary,
                        lineHeight: 1.4,
                      }}
                    >
                      {t('common:settings.clearAll')}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: theme.custom.typography.typeRoles.caption.size,
                        color: quiet,
                        lineHeight: 1.3,
                      }}
                    >
                      {t('common:settings.clearAllDescription')}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => { onClose(); onClearProjects(); }}
                  sx={{
                    fontWeight: theme.custom.typography.fontWeight.semibold,
                    fontSize: theme.custom.typography.typeRoles.bodySm.size,
                    textTransform: 'none',
                    minWidth: 72,
                    flexShrink: 0,
                    borderRadius: theme.custom.borders.radius.md,
                  }}
                >
                  {t('common:settings.purge')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <Snackbar
        open={Boolean(errorMsg)}
        autoHideDuration={6000}
        onClose={() => setErrorMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMsg(null)} severity="error" sx={{ width: '100%' }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
